import { useEffect, useRef, type ReactNode, type CSSProperties } from 'react';
import { Icon } from './Icon';

/**
 * Modal — composant unifié pour tous les dialogs/popups de la plateforme.
 *
 * Garanties :
 *  - centrage parfait (overlay flex center)
 *  - largeur contrainte par `size` (sm 460 / md 640 / lg 760 / xl 1040)
 *    avec fallback `min(Xpx, 92vw)` sur tous les viewports
 *  - hauteur max 90vh + body scrollable
 *  - close on Escape + click overlay
 *  - aria-modal + aria-labelledby si `title` fourni
 *  - support de header custom (titleRight pour boutons à côté du titre)
 *
 * Usage :
 *   <Modal open={isOpen} onClose={...} title="Titre" size="md">
 *     <ModalBody>contenu scrollable</ModalBody>
 *     <ModalFooter>boutons</ModalFooter>
 *   </Modal>
 */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<ModalSize, number> = {
  sm: 460,
  md: 640,
  lg: 760,
  xl: 1040,
};

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Texte du header. Si omis, pas de header rendu (caller doit gérer). */
  title?: ReactNode;
  /** Sous-titre optionnel sous le titre. */
  subtitle?: ReactNode;
  /** Slot à droite du titre (ex : badges, copy button, status). */
  titleRight?: ReactNode;
  /** Cacher le bouton X (rare ; ne pas faire sauf cas spécial). */
  hideCloseButton?: boolean;
  /** Préset de largeur. md (640) par défaut. */
  size?: ModalSize;
  /** Désactive la fermeture via clic sur l'overlay (ex : confirmation forcée). */
  disableOverlayClose?: boolean;
  /** Style overrides for advanced cases (rare). */
  panelStyle?: CSSProperties;
  /** Aria-label si pas de title. */
  ariaLabel?: string;
  /** Anchor en haut de la viewport plutôt qu'au centre (CommandPalette). */
  topAnchored?: boolean;
  children: ReactNode;
}

export function Modal({
  open, onClose,
  title, subtitle, titleRight, hideCloseButton = false,
  size = 'md', disableOverlayClose = false, panelStyle,
  ariaLabel, topAnchored = false,
  children,
}: ModalProps) {
  const titleId = useRef(`modal-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Empêche le scroll du body sous le modal pour ne pas que la page derrière
    // bouge sous les doigts (mobile surtout).
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const px = SIZE_PX[size];

  return (
    <div
      onClick={(e) => {
        if (disableOverlayClose) return;
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed', inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: topAnchored ? 'flex-start' : 'center',
        justifyContent: 'center',
        background: 'rgba(7, 15, 30, 0.82)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        padding: topAnchored ? '10vh 16px 16px' : 16,
        overflowY: 'auto',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId.current : undefined}
        aria-label={!title ? ariaLabel : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--elev-4)',
          width: `min(${px}px, 100%)`,
          maxHeight: topAnchored ? 'min(80vh, calc(100vh - 12vh))' : 'min(90vh, calc(100vh - 32px))',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          ...panelStyle,
        }}
      >
        {(title || titleRight || !hideCloseButton) && (
          <header
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0,
              background: 'color-mix(in oklab, var(--surface-0) 50%, transparent)',
            }}
          >
            {title && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 auto', gap: 2 }}>
                <h3
                  id={titleId.current}
                  style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {title}
                </h3>
                {subtitle && (
                  <span style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {subtitle}
                  </span>
                )}
              </div>
            )}
            {titleRight && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {titleRight}
              </div>
            )}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Fermer (Escape)"
                title="Fermer (Escape)"
                style={{
                  width: 32, height: 32,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <Icon name="x" size={14} />
              </button>
            )}
          </header>
        )}

        {children}
      </div>
    </div>
  );
}

/* ─── Slots optionnels pour body & footer (pour structure cohérente) ──── */

export function ModalBody({ children, style, padded = true }: {
  children: ReactNode;
  style?: CSSProperties;
  padded?: boolean;
}) {
  return (
    <div
      style={{
        padding: padded ? '18px' : 0,
        flex: '1 1 auto',
        overflowY: 'auto',
        minHeight: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function ModalFooter({ children, style }: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        padding: '12px 18px',
        borderTop: '1px solid var(--border-subtle)',
        background: 'color-mix(in oklab, var(--surface-0) 50%, transparent)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        flexShrink: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
